import React, { useContext, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const ProtectedRoute: React.FC<{ 
  children: React.ReactNode; 
  resource?: string;
  action?: string;
  roleCode?: string;
}> = ({ 
  children, 
  resource,
  action = 'read',
  roleCode
}) => {
  const { user, hasPermission, hasRole, loading, isAdmin } = useContext(AuthContext);

  // Debug için
  useEffect(() => {
    if (!loading) {
      if (resource) {
        console.log(`ProtectedRoute check for resource "${resource}" action "${action}":`, {
          isAdmin,
          hasPermission: hasPermission(resource, action),
          user
        });
      }
      if (roleCode) {
        console.log(`ProtectedRoute check for roleCode "${roleCode}":`, {
          isAdmin,
          hasRole: hasRole(roleCode),
          user
        });
      }
    }
  }, [loading, resource, action, roleCode, isAdmin, hasPermission, hasRole, user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Kullanıcı giriş yapmamış
  if (!user) {
    console.log("Protected route: No user, redirecting to login");
    return <Navigate to="/login" />;
  }

  // Admin kontrolü - Admin her şeye erişebilir
  if (isAdmin) {
    return <>{children}</>;
  }

  // Kaynak ve eylem belirtilmişse ve kullanıcının bu kaynağa erişim izni yoksa
  if (resource && !hasPermission(resource, action)) {
    console.log(`Access denied for resource ${resource} with action ${action}`);
    return <Navigate to="/unauthorized" />;
  }

  // Rol kodu belirtilmişse ve kullanıcının bu rol kodu yoksa
  // NOT: Rol koduna göre kontrol yerine izin kontrolü daha öncelikli olmalı
  if (roleCode && !hasRole(roleCode) && !hasRelevantPermissions(roleCode, hasPermission)) {
    console.log(`Access denied for role code ${roleCode}`);
    return <Navigate to="/unauthorized" />;
  }

  return <>{children}</>;
};

// Rol koduna göre ilgili izinleri kontrol eden yardımcı fonksiyon
function hasRelevantPermissions(roleCode: string, hasPermission: (resource: string, action?: string) => boolean): boolean {
  switch (roleCode) {
    case 'ROLE_DESIGNER':
      return hasPermission('designer', 'read') || 
             hasPermission('products', 'read') || 
             hasPermission('colors', 'read') || 
             hasPermission('accessories', 'read');
    
    case 'ROLE_RESOURCER':
      return hasPermission('sourcing', 'read') || 
             hasPermission('vendors', 'read') || 
             hasPermission('fabrics', 'read');
    
    case 'ROLE_MODELIST':
      return hasPermission('modelist', 'read') || 
             hasPermission('products', 'read');
    
    case 'ROLE_PLANNER':
      return hasPermission('planlama', 'read') || 
             hasPermission('orders', 'read');
    
    case 'ROLE_PRODUCT_MANAGER':
      return hasPermission('products', 'read');
    
    case 'ROLE_ORDER_MANAGER':
      return hasPermission('orders', 'read');
    
    case 'ROLE_ADMIN':
      return hasPermission('admin', 'read');
    
    default:
      return false;
  }
}

export default ProtectedRoute;
